import React, { memo, useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Button } from 'primereact/button'
import { Message } from 'primereact/message'
import { RadioButton } from 'primereact/radiobutton'
import { useSelector } from 'react-redux'

import useText from '../../../../../common/hooks/useText'
import { isFetchBaseQueryError } from '../../../../../common/redux/api/fetchBaseQueryWithReauth'
import {
    ChargePointUser,
    useCompanyAddRoleToUserMutation,
    useCompanyCreateUserMutation,
    useCompanyInviteExistingUserToCompanyMutation,
} from '../../../../../common/redux/api/web-api'
import { confirm } from '../../../../../common/uikit/ConfirmDialog'
import { Dialog } from '../../../../../common/uikit/Dialog'
import { Input } from '../../../../../common/uikit/Input'
import { useToast } from '../../../../../common/uikit/ToastProvider'
import { RootState } from '../../../../redux/store'

const formId = 'create-user-form'

const userRoles = [
    { value: 'User', labelCode: 'lblUserTypeUser', testId: 'radio-user' },
    { value: 'Admin', labelCode: 'lblUserTypeAdmin', testId: 'radio-admin' },
    { value: 'AdminViewOnly', labelCode: 'lblUserTypeAdminViewOnly', testId: 'radio-admin-view-only' },
] as const

export type CreateUserDialogProps = {
    visible: boolean
    onSuccess: () => void | Promise<void>
    onHide: () => void
}

type CreateUserFormValues = {
    firstName: string
    lastName: string
    email: string
    mobile: string
    password: string
    confirmPassword: string
    roleInCompany: string
}

const defaultValues: CreateUserFormValues = {
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    password: '',
    confirmPassword: '',
    roleInCompany: '',
}

export const CreateUserDialog = memo(function CreateUserDialog({
    visible,
    onSuccess,
    onHide,
}: CreateUserDialogProps): React.ReactElement {
    const text = useText()
    const toast = useToast()
    const companyId = useSelector((state: RootState) => state.user.selectedCompanyId)
    const [createUser, { isLoading: isCreatingUser }] = useCompanyCreateUserMutation()
    const [addRole, { isLoading: isAddingRole }] = useCompanyAddRoleToUserMutation()
    const [inviteUser, { isLoading: isInvitingUser }] = useCompanyInviteExistingUserToCompanyMutation()
    const {
        control,
        getValues,
        handleSubmit,
        register,
        reset,
        formState: { errors, isSubmitted, isSubmitting, isValid },
    } = useForm<CreateUserFormValues>({
        defaultValues,
        mode: 'onSubmit',
        reValidateMode: 'onChange',
    })

    useEffect(() => {
        if (visible) reset(defaultValues)
    }, [reset, visible])

    const inviteExistingUser = async (chargePointUser: ChargePointUser) => {
        const confirmed = await confirm({
            header: text('lblProceedConfirmation'),
            message: text('msgShouldSendInviteToExistingUser'),
        })
        if (!confirmed) return

        try {
            await inviteUser({
                companyId,
                chargePointUser,
                role: chargePointUser.roleInCompany ?? '',
            }).unwrap()
            toast.notifySuccess(text('msgInviteSent'))
            await onSuccess()
        } catch {
            toast.notifyError(text('msgInviteNotSent'))
        }
    }

    const submit = async (values: CreateUserFormValues) => {
        const chargePointUser: ChargePointUser = {
            firstName: values.firstName,
            lastName: values.lastName,
            email: values.email,
            mobile: values.mobile || null,
            password: values.password,
            roleInCompany: values.roleInCompany,
            userStatus: 'Valid',
        }

        let createdUser: ChargePointUser
        try {
            createdUser = (await createUser({ companyId, chargePointUser }).unwrap()) as unknown as ChargePointUser
        } catch (error: unknown) {
            if (isFetchBaseQueryError(error) && error.status === 412) {
                toast.notifyError(text('msgUserAlreadyInOrg'))
                return
            }
            if (isFetchBaseQueryError(error) && error.status === 409) {
                await inviteExistingUser(chargePointUser)
                return
            }

            toast.notifyError(text('msgUserNotSaved'))
            return
        }

        if (!createdUser.id) {
            toast.notifyError(text('msgUserNotSaved'))
            return
        }

        try {
            await addRole({
                companyId,
                userId: createdUser.id,
                role: {
                    claim: values.roleInCompany,
                    companyId,
                    chargePointUserId: createdUser.id,
                },
            }).unwrap()
            toast.notifySuccess(text('lblNewUsercreated'))
            await onSuccess()
        } catch {
            toast.notifyError(text('msgUserNotSaved'))
        }
    }

    const isLoading = isCreatingUser || isAddingRole || isInvitingUser || isSubmitting
    const submitDisabled = isLoading || (isSubmitted && !isValid)
    const footer = (
        <div className="flex justify-content-end gap-2">
            <Button label={text('btnCancel')} type="button" text onClick={onHide} />
            <Button
                label={text('btnCreate')}
                type="submit"
                form={formId}
                disabled={submitDisabled}
                loading={isLoading}
            />
        </div>
    )

    return (
        <Dialog
            header={text('lblNewUserDialog')}
            visible={visible}
            onHide={onHide}
            footer={footer}
            data-testid="create-user-dialog"
        >
            <form id={formId} onSubmit={handleSubmit(submit)} noValidate>
                <div className="formgrid grid">
                    <div className="field col-12 md:col-6">
                        <Input
                            id="create-user-first-name"
                            label={text('lblFirstName')}
                            required
                            error={errors.firstName?.message}
                            {...register('firstName', { required: text('msgFirstNameRequired') })}
                        />
                    </div>
                    <div className="field col-12 md:col-6">
                        <Input
                            id="create-user-last-name"
                            label={text('lblLastNameField')}
                            required
                            error={errors.lastName?.message}
                            {...register('lastName', { required: text('msgLastNameRequired') })}
                        />
                    </div>
                    <div className="field col-12 md:col-6">
                        <Input
                            id="create-user-email"
                            type="email"
                            label={text('lblEmail')}
                            autoComplete="email"
                            required
                            error={errors.email?.message}
                            {...register('email', { required: text('msgEmailRequired') })}
                        />
                    </div>
                    <div className="field col-12 md:col-6">
                        <Input
                            id="create-user-mobile"
                            type="tel"
                            label={text('lblMobile')}
                            autoComplete="tel"
                            {...register('mobile')}
                        />
                    </div>
                    <div className="field col-12 md:col-6">
                        <Input
                            id="create-user-password"
                            type="password"
                            label={text('lblNewPasswordField')}
                            autoComplete="new-password"
                            subtitle={text('msgMinimumPasswordLength')}
                            required
                            error={errors.password?.message}
                            {...register('password', {
                                required: text('msgPasswordRequired'),
                                minLength: { value: 8, message: text('msgPasswordToShort') },
                            })}
                        />
                    </div>
                    <div className="field col-12 md:col-6">
                        <Input
                            id="create-user-confirm-password"
                            type="password"
                            label={text('lblConfirmPasswordField')}
                            autoComplete="new-password"
                            required
                            error={errors.confirmPassword?.message}
                            {...register('confirmPassword', {
                                required: text('msgPasswordDoNotMatch'),
                                deps: 'password',
                                validate: (value) => value === getValues('password') || text('msgPasswordDoNotMatch'),
                            })}
                        />
                    </div>
                    <div className="field col-12 mb-0" role="group" aria-labelledby="create-user-role-label">
                        <span id="create-user-role-label" className="p-label block mb-2 required-label">
                            {text('lblRole')}
                        </span>
                        <Controller
                            name="roleInCompany"
                            control={control}
                            rules={{ required: text('msgRequiredShort') }}
                            render={({ field }) => (
                                <div className="flex flex-column gap-2">
                                    {userRoles.map((role) => (
                                        <div key={role.value} className="flex align-items-center gap-2">
                                            <RadioButton
                                                inputId={`create-user-role-${role.value}`}
                                                value={role.value}
                                                name={field.name}
                                                checked={field.value === role.value}
                                                onBlur={field.onBlur}
                                                onChange={(event) => field.onChange(event.value)}
                                                data-testid={role.testId}
                                            />
                                            <label htmlFor={`create-user-role-${role.value}`}>
                                                {text(role.labelCode)}
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            )}
                        />
                        {errors.roleInCompany?.message ? (
                            <Message severity="error" text={errors.roleInCompany.message} className="mt-2" />
                        ) : null}
                    </div>
                </div>
            </form>
        </Dialog>
    )
})
